import React, { useEffect, useRef, useState } from "react";
import { api, RSD } from "../../lib/api";
import { useCart } from "../../context/CartContext";
import { useAuth, startGoogleLogin } from "../../context/AuthContext";
import PhoneVerify from "./PhoneVerify";
import EmailVerify from "./EmailVerify";
import { enablePush } from "../../lib/push";
import { X, Zap, Clock, Banknote, CreditCard, Phone, Check, Pencil, ChevronLeft } from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "../../context/I18nContext";
import { getPosition } from "../../lib/geo";
import { useCity } from "../../context/CityContext";
import { useInstallSuppress } from "../../lib/pwaInstall";
import { track, trackNow, analyticsSessionId } from "../../lib/analytics";

export default function CheckoutSheet({ open, onClose, onEdit, onPlaced, canOrder, blockMsg, restaurant }) {
  const { items, total, clear, restaurant: cartRestaurant, orderNote, prefs, setPrefs } = useCart();
  const { t, locale } = useI18n();
  const orderRest = cartRestaurant || restaurant;
  const { user, setUser } = useAuth();
  const [phone, setPhone] = useState("");
  const [pickupType, setPickupType] = useState(null);
  const [slots, setSlots] = useState([]);
  const [slotInfo, setSlotInfo] = useState({});
  const [slot, setSlot] = useState(null);
  const [payment, setPayment] = useState(null);
  const [payUnavailable, setPayUnavailable] = useState(false);
  const [errors, setErrors] = useState({});
  const [placing, setPlacing] = useState(false);
  const [verifyOpen, setVerifyOpen] = useState(false);
  const [emailVerifyOpen, setEmailVerifyOpen] = useState(false);
  const [confirmPrice, setConfirmPrice] = useState(false);
  const [needsTerms, setNeedsTerms] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [cityWarn, setCityWarn] = useState(null);
  const { selectCity } = useCity();
  const clientId = useRef(null);
  useInstallSuppress(open);

  const fetchSlots = () => api.get(`/restaurants/${orderRest?.slug}/pickup-slots`)
    .then(({ data }) => { setSlots(data.slots || []); setSlotInfo(data || {}); })
    .catch(() => {});

  useEffect(() => {
    if (open) {
      if (!clientId.current) clientId.current = (crypto.randomUUID ? crypto.randomUUID() : String(Date.now() + Math.random()));
      setPhone(user?.phone || "");
      fetchSlots();
      if (user) {
        api.get("/legal/status")
          .then(({ data }) => { setNeedsTerms(!data.terms_accepted); setTermsAccepted(false); })
          .catch(() => {});
      }
    }
    // eslint-disable-next-line
  }, [open, user, restaurant?.busy_mode_enabled]);

  const paySetting = (slotInfo.payment_method || orderRest?.payment_method || "BOTH").toUpperCase();
  const allowCash = paySetting === "CASH" || paySetting === "BOTH";
  const allowCard = paySetting === "CARD" || paySetting === "BOTH";

  // Restore previously chosen preferences (in-progress selections or "Order again" defaults).
  // Never auto-select anything for a fresh cart; the customer must tap explicitly.
  useEffect(() => {
    if (!open) return;
    if (prefs?.pickup_type && !pickupType) {
      // "Order again" must never restore ASAP for a cart that is now above the threshold.
      if (!(prefs.pickup_type === "asap" && asapBlocked)) setPickupType(prefs.pickup_type);
    }
    const pm = prefs?.payment_method;
    if (!pm) return;
    const stillOk = (pm === "cash" && allowCash) || (pm === "card" && allowCard);
    if (stillOk) { if (!payment) setPayment(pm); setPayUnavailable(false); }
    else { setPayment(null); setPayUnavailable(true); setPrefs({ payment_method: null }); }
    // eslint-disable-next-line
  }, [open, paySetting, prefs?.pickup_type, prefs?.payment_method]);

  // ASAP is unavailable for large orders (server-authoritative threshold, mirrored here for UX).
  // Never auto-selects another time: the customer must pick a pickup slot explicitly.
  const asapLimit = Number(slotInfo.asap_max_order_total ?? 3000);
  const asapBlocked = total > asapLimit;
  useEffect(() => {
    if (asapBlocked) {
      // Observed only — the >3.000 RSD rule itself stays enforced on the backend.
      track("asap_unavailable_large_order", {
        restaurant_id: orderRest?.id, cart_value: Math.round(total), item_count: items.length,
        threshold: asapLimit, dedupe_key: `${orderRest?.id}:large-cart`,
      });
    }
    if (asapBlocked && pickupType === "asap") {
      setPickupType(null);
      setSlot(null);
      setPrefs({ pickup_type: null });
    }
    // eslint-disable-next-line
  }, [asapBlocked, pickupType]);

  const choosePickup = (type) => {
    if (type === "asap" && asapBlocked) return;
    setPickupType(type);
    if (type === "asap") setSlot(null);
    setPrefs({ pickup_type: type });
    setErrors((e) => ({ ...e, pickup: false }));
    track("pickup_mode_selected", { restaurant_id: orderRest?.id, pickup_type: type });
  };
  const choosePayment = (pm) => {
    setPayment(pm);
    setPayUnavailable(false);
    setPrefs({ payment_method: pm });
    setErrors((e) => ({ ...e, payment: false }));
    track("payment_method_selected", { restaurant_id: orderRest?.id, payment_method: pm });
  };

  const pickupReady = pickupType === "asap" || (pickupType === "scheduled" && !!slot);
  const selectionsReady = pickupReady && !!payment;

  if (!open) return null;

  const nameOf = (it) => (locale === "en" ? (it.name_en || it.name) : it.name);
  const optsOf = (it) => {
    const src = locale === "en" && it.option_names_en?.length ? it.option_names_en : it.option_names;
    return src || [];
  };
  const asapLabel = t("checkout.asap_label", "Što pre");
  const notSelected = t("checkout.not_selected", "Nije izabrano");
  const pickupLabel = pickupType === "scheduled"
    ? (slot || notSelected)
    : pickupType === "asap"
      ? (slotInfo.asap_time ? `${asapLabel} — ${slotInfo.asap_time}` : asapLabel)
      : notSelected;

  const savePhone = async () => {
    if (!phone.trim()) { toast.error(t("checkout.enter_phone", "Unesite broj telefona")); return false; }
    try {
      await api.patch("/auth/phone", { phone });
      setUser({ ...user, phone });
      return true;
    } catch { toast.error(t("checkout.save_phone_err", "Greška pri čuvanju broja")); return false; }
  };

  const place = async (confirmTotal = false) => {
    // Flushed immediately: a submit can navigate away (login redirect) before the next batch.
    trackNow("checkout_submit_attempted", { restaurant_id: orderRest?.id, items: items.length,
                                            cart_value: Math.round(total), confirm_retry: !!confirmTotal });
    if (!user) { const rt = window.location.pathname + window.location.search; sessionStorage.setItem("ponesi_return_to", rt); startGoogleLogin(rt); return; }
    if (!user.phone) { const ok = await savePhone(); if (!ok) return; }
    if (!pickupReady) {
      setErrors({ pickup: true });
      track("checkout_validation_failed", { restaurant_id: orderRest?.id, reason: "pickup_missing" });
      toast.error(t("checkout.err_pickup", "Izaberite vreme preuzimanja."));
      return;
    }
    if (!payment) {
      setErrors({ payment: true });
      track("checkout_validation_failed", { restaurant_id: orderRest?.id, reason: "payment_missing" });
      toast.error(t("checkout.err_payment", "Izaberite način plaćanja."));
      return;
    }
    if (needsTerms && !termsAccepted) {
      track("checkout_validation_failed", { restaurant_id: orderRest?.id, reason: "terms_required" });
      toast.error(t("checkout.terms_required", "Morate prihvatiti Uslove korišćenja da biste poručili")); return; }
    if (placing) return;
    setPlacing(true);
    try {
      if (needsTerms && termsAccepted) {
        try { await api.post("/legal/accept"); setNeedsTerms(false); } catch (err) { setPlacing(false); toast.error(t("common.error", "Došlo je do greške")); return; }
      }
      let geo = null;
      try { geo = await getPosition(); } catch { geo = null; }
      const payload = {
        client_order_id: clientId.current,
        restaurant_slug: orderRest?.slug,
        customer_lat: geo?.lat ?? null,
        customer_lng: geo?.lng ?? null,
        items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity, selected_options: i.option_ids, note: i.note, person_label: i.person || "" })),
        pickup_type: pickupType,
        pickup_time: pickupType === "scheduled" ? slot : null,
        payment_method: payment,
        expected_total: total,
        confirm_total: confirmTotal,
        customer_note: orderNote,
        analytics_session_id: analyticsSessionId(),
      };
      const { data } = await api.post("/orders", payload);
      clear();
      clientId.current = null;
      toast.success(locale === "en" ? `Order #${data.order_number} sent!` : `Porudžbina #${data.order_number} poslata!`);
      onPlaced(data);
      enablePush().catch(() => {});
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const code = detail?.code;
      if (e?.response?.status === 403 && code === "EMAIL_VERIFICATION_REQUIRED") {
        setEmailVerifyOpen(true);
      } else if (e?.response?.status === 403 && code === "PHONE_VERIFICATION_REQUIRED") {
        setVerifyOpen(true);
      } else if (code === "PRICE_CHANGED") {
        toast.warning(`${detail.message} ${locale === "en" ? "New total" : "Nova cena"}: ${RSD(detail.server_total)}.`, { duration: 6000 });
        setConfirmPrice(true);
      } else if (code === "BUSY_MODE_PICKUP_TIME_TOO_SOON" || code === "PICKUP_TIME_NO_LONGER_AVAILABLE") {
        setSlot(null);
        fetchSlots();
        toast.warning(detail.message + (detail.earliest_pickup ? ` (${detail.earliest_pickup})` : ""), { duration: 6000 });
      } else if (e?.response?.status === 403 && code === "TERMS_ACCEPTANCE_REQUIRED") {
        setNeedsTerms(true);
        toast.error(t("checkout.terms_required", "Morate prihvatiti Uslove korišćenja da biste poručili"));
      } else if (e?.response?.status === 409 && code === "CITY_MISMATCH") {
        setCityWarn(detail);
      } else if (code === "PAYMENT_METHOD_NOT_SUPPORTED") {
        fetchSlots();
        const allowed = (detail && detail.payment_method) || "BOTH";
        setPayment(null);
        setPayUnavailable(true);
        setPrefs({ payment_method: null });
        void allowed;
        toast.warning(t("checkout.payment_changed", "Restoran više ne podržava izabrani način plaćanja. Izaberite dostupni način."), { duration: 6000 });
      } else if (code === "PRODUCT_UNAVAILABLE") {
        // Authoritative backend signal ONLY. Never mutate the cart on the client — a transient
        // reload or the email/phone-verification round-trip must never drop a valid item.
        // Surface the message and keep the cart intact so the customer can review and retry.
        toast.error(locale === "en" ? t("checkout.product_issue", "An item is currently unavailable. Please review your cart and try again.") : ((detail && detail.message) || t("checkout.product_issue", "Neki proizvod trenutno nije dostupan. Proverite korpu i pokušajte ponovo.")), { duration: 6000 });
      } else {
        const msg = (detail && (detail.message || (typeof detail === "string" && detail))) || t("checkout.generic_err", "Porudžbina trenutno nije mogla da bude poslata. Pokušajte ponovo.");
        toast.error(msg);
      }
    } finally {
      setPlacing(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 max-w-md mx-auto" data-testid="checkout-sheet">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="absolute bottom-0 left-0 right-0 bg-white rounded-t-3xl max-h-[90vh] overflow-y-auto animate-slide-up">
        <div className="flex items-center gap-2 p-5 border-b sticky top-0 bg-white z-10">
          <button onClick={() => (onEdit ? onEdit() : onClose())} data-testid="back-to-cart" aria-label={t("checkout.back", "Nazad na korpu")}
            className="bg-slate-100 rounded-full p-2 active:scale-95 transition-transform"><ChevronLeft size={18} /></button>
          <h2 className="font-display font-black text-xl flex-1">{t("checkout.title", "Naručivanje")}</h2>
          <button onClick={onClose} data-testid="close-checkout" className="bg-slate-100 rounded-full p-2"><X size={18} /></button>
        </div>

        <div className="p-5 space-y-6">
          {!user && (
            <div className="bg-brand-red/5 rounded-2xl p-4 text-center">
              <p className="text-sm text-slate-600 mb-3">{t("checkout.login_prompt", "Prijavite se da biste naručili")}</p>
              <button data-testid="google-login-btn" onClick={() => { const rt = window.location.pathname + window.location.search; sessionStorage.setItem("ponesi_return_to", rt); startGoogleLogin(rt); }}
                className="w-full bg-white border-2 border-slate-200 rounded-full py-3 font-bold flex items-center justify-center gap-2">
                <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="" className="w-5 h-5" />
                {t("checkout.google", "Prijava putem Google-a")}
              </button>
            </div>
          )}

          {user && !user.phone && (
            <div>
              <h3 className="font-display font-bold mb-2 flex items-center gap-2"><Phone size={16} /> {t("checkout.phone", "Broj telefona")}</h3>
              <input data-testid="phone-input" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+381 6x xxx xxxx"
                className="w-full px-4 py-3 rounded-2xl bg-slate-100 outline-none focus:ring-2 focus:ring-brand-red" />
              <p className="text-xs text-slate-400 mt-1">{t("checkout.phone_hint", "Potreban za obaveštenje o porudžbini.")}</p>
            </div>
          )}

          <div className={errors.pickup ? "rounded-2xl ring-2 ring-brand-red p-3 -m-3" : ""} data-testid="pickup-section">
            <h3 className="font-display font-bold mb-2">{t("checkout.pickup_time", "Vreme preuzimanja")}</h3>
            {slotInfo.busy_mode && (
              <div data-testid="busy-notice" className="mb-3 bg-brand-orange/10 text-brand-orange rounded-xl px-3 py-2 text-sm font-semibold">
                {t("checkout.busy_notice", "Restoran je trenutno u gužvi. Najranije moguće preuzimanje je za 30 minuta.")}
              </div>
            )}
            <div className="grid grid-cols-2 gap-2">
              <button data-testid="pickup-asap" disabled={asapBlocked} onClick={() => choosePickup("asap")}
                className={`rounded-2xl py-3 font-bold border-2 flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed ${pickupType === "asap" ? "border-brand-red bg-brand-red/5 text-brand-red" : "border-slate-200"}`}>
                <Zap size={16} /> {t("checkout.asap", "Što pre")}{pickupType === "asap" && slotInfo.asap_time ? ` — ${slotInfo.asap_time}` : ""}
              </button>
              <button data-testid="pickup-scheduled" onClick={() => choosePickup("scheduled")}
                className={`rounded-2xl py-3 font-bold border-2 flex items-center justify-center gap-2 ${pickupType === "scheduled" ? "border-brand-red bg-brand-red/5 text-brand-red" : "border-slate-200"}`}>
                <Clock size={16} /> {t("checkout.schedule", "Zakaži")}
              </button>
            </div>
            {asapBlocked && (
              <div data-testid="asap-blocked-notice" className="mt-2 bg-brand-orange/10 text-brand-orange rounded-xl px-3 py-2 text-sm font-semibold">
                {t("checkout.asap_blocked", "Za porudžbine preko 3.000 RSD izaberite vreme preuzimanja.")}
                <span className="block font-normal opacity-80">
                  {t("checkout.asap_blocked_hint", "Veće porudžbine zahtevaju više vremena za pripremu.")}
                </span>
              </div>
            )}
            {errors.pickup && <p data-testid="pickup-error" className="text-sm font-semibold text-brand-red mt-2">{t("checkout.err_pickup", "Izaberite vreme preuzimanja.")}</p>}
            {pickupType === "scheduled" && (
              <div className="grid grid-cols-4 gap-2 mt-3">
                {slots.length === 0 && <p className="col-span-4 text-sm text-slate-400">{t("checkout.no_slots", "Nema slobodnih termina.")}</p>}
                {slots.map((s) => (
                  <button key={s} data-testid={`slot-${s}`} onClick={() => { setSlot(s); setErrors((e) => ({ ...e, pickup: false })); }}
                    className={`rounded-xl py-2 text-sm font-bold border-2 ${slot === s ? "border-brand-red bg-brand-red text-white" : "border-slate-200"}`}>{s}</button>
                ))}
              </div>
            )}
          </div>

          <div className={errors.payment ? "rounded-2xl ring-2 ring-brand-red p-3 -m-3" : ""} data-testid="payment-section">
            <h3 className="font-display font-bold mb-2">{t("checkout.payment", "Način plaćanja")}</h3>
            {payUnavailable && (
              <div data-testid="payment-unavailable" className="mb-2 bg-brand-orange/10 text-brand-orange rounded-xl px-3 py-2 text-sm font-semibold">
                {t("checkout.pay_unavailable", "Prethodno izabrani način plaćanja više nije dostupan. Izaberite novi način plaćanja.")}
              </div>
            )}
            <div className="space-y-2">
              {allowCash && (
                <button data-testid="pay-cash" onClick={() => choosePayment("cash")}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-2xl border-2 ${payment === "cash" ? "border-brand-green bg-brand-green/5" : "border-slate-200"}`}>
                  <Banknote size={18} /> <span className="font-medium">{t("checkout.cash", "Keš pri preuzimanju")}</span>
                  {payment === "cash" && <Check size={18} className="ml-auto text-brand-green" />}
                </button>
              )}
              {allowCard && (
                <button data-testid="pay-card" onClick={() => choosePayment("card")}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-2xl border-2 ${payment === "card" ? "border-brand-green bg-brand-green/5" : "border-slate-200"}`}>
                  <CreditCard size={18} /> <span className="font-medium">{t("checkout.card", "Kartica u restoranu")}</span>
                  {payment === "card" && <Check size={18} className="ml-auto text-brand-green" />}
                </button>
              )}
            </div>
            {errors.payment && <p data-testid="payment-error" className="text-sm font-semibold text-brand-red mt-2">{t("checkout.err_payment", "Izaberite način plaćanja.")}</p>}
          </div>

          {needsTerms && (
            <label className="flex gap-2.5 items-start bg-brand-red/5 rounded-2xl p-4 cursor-pointer" data-testid="terms-accept-row">
              <input type="checkbox" data-testid="terms-checkbox" checked={termsAccepted}
                onChange={(e) => setTermsAccepted(e.target.checked)} className="mt-0.5 w-4 h-4 accent-brand-red shrink-0" />
              <span className="text-sm text-slate-700 leading-snug">
                {t("checkout.terms_pre", "Prihvatam ")}
                <a href={locale === "en" ? "/en/terms" : "/uslovi-koriscenja"} target="_blank" rel="noopener noreferrer" data-testid="terms-link" className="text-brand-red font-semibold underline">{t("checkout.terms_link", "Uslove korišćenja")}</a>
                {t("checkout.terms_mid", " i potvrđujem da sam upoznat/a sa ")}
                <a href={locale === "en" ? "/en/privacy" : "/politika-privatnosti"} target="_blank" rel="noopener noreferrer" data-testid="privacy-link" className="text-brand-red font-semibold underline">{t("checkout.privacy_link", "Politikom privatnosti")}</a>
                {t("checkout.terms_post", ".")}
              </span>
            </label>
          )}
        </div>

        {cityWarn && (
          <div className="fixed inset-0 z-[70] max-w-md mx-auto" data-testid="city-mismatch-modal">
            <div className="absolute inset-0 bg-black/50" onClick={() => setCityWarn(null)} />
            <div className="absolute bottom-0 left-0 right-0 bg-white rounded-t-3xl p-5 animate-slide-up">
              <h3 className="font-display font-black text-lg text-brand-red">{locale === "en" ? "Wrong city for pickup" : "Pogrešan grad za preuzimanje"}</h3>
              <p className="text-sm text-slate-700 mt-2">
                {locale === "en"
                  ? `You are currently in ${cityWarn.detected_city || "another city"}, but this restaurant is in ${cityWarn.restaurant_city}.`
                  : `Trenutno se nalazite u ${cityWarn.detected_city || "drugom gradu"}, a restoran je u ${cityWarn.restaurant_city}.`}
              </p>
              <p className="text-sm text-slate-500 mt-1">{locale === "en" ? "Ponesi is designed for pickup orders." : "Ponesi je namenjen ličnom preuzimanju porudžbine."}</p>
              <div className="grid grid-cols-1 gap-2 mt-4">
                {cityWarn.detected_city_id && (
                  <button data-testid="city-return-btn" onClick={() => { selectCity(cityWarn.detected_city_id); setCityWarn(null); onClose(); window.location.href = locale === "en" ? "/en" : "/"; }}
                    className="rounded-full py-3 bg-brand-red text-white font-bold">{(locale === "en" ? "Return to " : "Vrati se na ") + cityWarn.detected_city}</button>
                )}
                <button data-testid="city-recheck-btn" onClick={() => { setCityWarn(null); place(confirmPrice); }}
                  className="rounded-full py-3 bg-slate-100 font-bold">{locale === "en" ? "Try again" : "Proveri lokaciju ponovo"}</button>
              </div>
            </div>
          </div>
        )}

        {items.length > 0 && (
          <div className="px-5 pb-2" data-testid="order-summary">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-display font-black text-base uppercase tracking-wide">{t("checkout.summary_title", "Pregled porudžbine")}</h3>
              {onEdit && (
                <button data-testid="summary-edit" onClick={onEdit} className="text-sm font-bold text-brand-red flex items-center gap-1">
                  <Pencil size={13} /> {t("checkout.edit", "Uredi")}
                </button>
              )}
            </div>
            <div className="rounded-2xl bg-slate-50 border border-slate-100 divide-y divide-slate-100">
              {items.map((it) => (
                <div key={it.uid} className="p-3" data-testid={`summary-item-${it.product_id}`}>
                  <div className="flex justify-between gap-2">
                    <p className="font-semibold text-slate-900">{it.quantity} × {nameOf(it)}</p>
                    <p className="font-display font-bold text-slate-900 shrink-0">{RSD(it.unit_computed * it.quantity)}</p>
                  </div>
                  {optsOf(it).length > 0 && (
                    <ul className="mt-0.5">
                      {optsOf(it).map((o, i) => (
                        <li key={i} className="text-sm text-slate-500 flex items-center gap-1.5"><span className="text-brand-red">•</span> {o}</li>
                      ))}
                    </ul>
                  )}
                  {it.person && <p className="text-xs mt-0.5"><span className="font-bold text-brand-red">{t("item.for_prefix", "Za")}: </span><span className="font-semibold text-slate-700">{it.person}</span></p>}
                  {it.note && <p className="text-xs text-slate-500 mt-0.5"><span className="font-semibold">{t("item.note_prefix", "Napomena")}: </span><span className="italic">{it.note}</span></p>}
                </div>
              ))}
              <div className="p-3 flex justify-between text-sm">
                <span className="text-slate-500">{t("checkout.pickup_short", "Vreme preuzimanja")}</span>
                <span className="font-semibold text-slate-800" data-testid="summary-pickup">{pickupLabel}</span>
              </div>
              <div className="p-3 flex justify-between text-sm">
                <span className="text-slate-500">{t("checkout.payment_short", "Plaćanje")}</span>
                <span className="font-semibold text-slate-800" data-testid="summary-payment">{payment === "cash" ? t("checkout.cash", "Keš pri preuzimanju") : payment === "card" ? t("checkout.card", "Kartica u restoranu") : notSelected}</span>
              </div>
              {orderNote && (
                <div className="p-3" data-testid="summary-note">
                  <div className="text-[10px] font-black uppercase tracking-wider text-brand-orange">{t("checkout.note_summary", "Napomena za restoran")}</div>
                  <p className="text-sm text-slate-700 mt-0.5 break-words">"{orderNote}"</p>
                </div>
              )}
            </div>
          </div>
        )}

        <div className="p-5 border-t sticky bottom-0 bg-white">
          <div className="flex items-center justify-between mb-3">
            <span className="text-slate-500">{t("checkout.total", "Ukupno")}</span>
            <span className="font-display font-black text-xl">{RSD(total)}</span>
          </div>
          {/* Enabled even with missing selections: place() validates and highlights the missing
              section (ring + toast), which is also what makes the validation events measurable. */}
          <button data-testid="place-order-btn" disabled={placing || canOrder === false} onClick={() => place(confirmPrice)}
            className="w-full bg-brand-red text-white rounded-full py-4 font-display font-bold active:scale-[0.98] transition-transform disabled:opacity-50">
            {canOrder === false ? (blockMsg || t("checkout.cant_order", "Naručivanje nije moguće")) : placing ? t("checkout.sending", "Slanje…") : confirmPrice ? t("checkout.confirm_place", "Potvrdi i poruči") : t("checkout.place", "Poruči")}
          </button>
        </div>
      </div>
      <PhoneVerify open={verifyOpen} initialPhone={user?.phone} onClose={() => setVerifyOpen(false)}
        onVerified={(p) => { setUser({ ...user, phone: p, phone_verified: true }); setVerifyOpen(false); place(); }} />
      <EmailVerify open={emailVerifyOpen} email={user?.email} onClose={() => setEmailVerifyOpen(false)}
        onVerified={() => { setUser({ ...user, ponesi_email_verified: true, ponesi_verified_email: user?.email, email_verification_required: false }); setEmailVerifyOpen(false); place(); }} />
    </div>
  );
}
